import React, { useState, useEffect, useMemo } from 'react';
import {
	MaterialReactTable,
	useMaterialReactTable,
} from 'material-react-table';
import { useSearchParams } from 'react-router-dom';
import { makeApiRequest } from '../../Shared/Api';
import {
	Box,
	Button,
	IconButton,
	Tooltip,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import {
	Edit as EditIcon,
	Delete as DeleteIcon,
	Add as AddIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import EditCustomer from '../../Components/Modals/EditCustomer';
import MobileCreateFab from '../../Components/MobileCreateFab';
import useConfirm from '../../Components/useConfirm';
import { useAppContext, ACTION_TYPES } from '../../Context/AppContext';
import { refreshOnboardingChecklist } from '../../Shared/onboarding';
import CustomersMobileList from './components/CustomersMobileList';

const Customers = () => {
	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
	const [searchParams, setSearchParams] = useSearchParams();
	const [customers, setCustomers] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [editCustomerModalOpen, setEditCustomerModalOpen] = useState(false);
	const [selectedCustomer, setSelectedCustomer] = useState(null);
	const { dispatch } = useAppContext();
	const { confirm, confirmDialog } = useConfirm();
	const { enqueueSnackbar } = useSnackbar();

	const fetchCustomers = async () => {
		setIsLoading(true);
		try {
			const response = await makeApiRequest('GET', '/customers/');
			setCustomers(response || []);
		} catch (error) {
			console.error('Failed to fetch customers:', error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		dispatch({
			type: ACTION_TYPES.SET_PAGE_TITLE,
			payload: { pageTitle: 'Customers' },
		});
		fetchCustomers();
	}, [dispatch]);

	useEffect(() => {
		if (searchParams.get('onboarding') !== '1') {
			return;
		}
		setSelectedCustomer(null);
		setEditCustomerModalOpen(true);
		setSearchParams({}, { replace: true });
	}, [searchParams, setSearchParams]);

	const handleCustomerSaved = () => {
		fetchCustomers();
		refreshOnboardingChecklist();
	};

	const handleOpen = (customer = null) => {
		setSelectedCustomer(customer);
		setEditCustomerModalOpen(true);
	};

	const handleClose = () => {
		setEditCustomerModalOpen(false);
		setSelectedCustomer(null);
	};

	const handleDelete = async (customerId) => {
		const confirmed = await confirm(
			'Are you sure you want to delete this customer?',
			{
				title: 'Delete Customer',
				confirmText: 'Delete',
			},
		);
		if (!confirmed) return;

		try {
			await makeApiRequest('DELETE', `/customers/${customerId}/`);
			enqueueSnackbar('Customer deleted successfully', { variant: 'success' });
			fetchCustomers();
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to delete customer', {
				variant: 'error',
			});
		}
	};

	const columns = useMemo(
		() => [
			{
				header: 'Name',
				accessorKey: 'name',
			},
			{
				header: 'Phone',
				accessorKey: 'phone',
			},
			{
				header: 'Email',
				accessorKey: 'email',
			},
			{
				header: 'Invoicing Email',
				accessorKey: 'invoicing_email',
				Cell: ({ cell }) => cell.getValue() || '—',
			},
		],
		[],
	);

	const renderCreateCustomerButton = () => (
		<Button
			variant="contained"
			startIcon={<AddIcon />}
			onClick={() => handleOpen()}
			sx={{
				textTransform: 'none',
				fontWeight: 600,
			}}
		>
			Create Customer
		</Button>
	);

	const table = useMaterialReactTable({
		columns,
		data: customers,
		layoutMode: 'semantic',
		enableRowActions: true,
		positionActionsColumn: 'last',
		state: {
			isLoading,
		},
		renderRowActions: ({ row }) => (
			<Box sx={{ display: 'flex', gap: '1rem' }}>
				<Tooltip title="Edit">
					<IconButton onClick={() => handleOpen(row.original)}>
						<EditIcon />
					</IconButton>
				</Tooltip>
				<Tooltip title="Delete">
					<IconButton
						color="error"
						onClick={() => handleDelete(row.original.id)}
					>
						<DeleteIcon />
					</IconButton>
				</Tooltip>
			</Box>
		),
		renderTopToolbarCustomActions: renderCreateCustomerButton,
		muiTablePaperProps: {
			elevation: 2,
			sx: {
				borderRadius: '8px',
			},
		},
		muiTableHeadCellProps: {
			sx: {
				fontWeight: 'bold',
				fontSize: '0.95rem',
			},
		},
	});

	return (
		<Box sx={{ width: '100%' }}>
			{isDesktop ? (
				<MaterialReactTable table={table} />
			) : (
				<>
					<CustomersMobileList
						customers={customers}
						isLoading={isLoading}
						onEdit={handleOpen}
						onDelete={handleDelete}
					/>
					<MobileCreateFab
						label="Create Customer"
						onClick={() => handleOpen()}
					/>
				</>
			)}
			<EditCustomer
				open={editCustomerModalOpen}
				onClose={handleClose}
				onSubmit={handleCustomerSaved}
				selectedCustomer={selectedCustomer}
			/>
			{confirmDialog}
		</Box>
	);
};

export default Customers;
